import {
	keepPreviousData,
	queryOptions,
	useMutation,
	useQueryClient,
} from '@tanstack/react-query';

import { parse } from '@tinyhttp/content-disposition';
import { api } from '#client';
import { handleError, parseSchema } from '#client/validation';

import { Activity } from '../shared/activity.types';
import { Reasons } from '../shared/reasons.types';
import { ReportStatusBody, ReportSubmitBody } from '../shared/report.types';
import {
	CalculationTypes,
	ClaimBody,
	ClaimCatCodes,
	ClaimCauseOfLossCodes,
	ClaimsQuery,
	ClaimsResults,
	ClaimStatuses,
	CloseClaimBody,
	NewClaimResponse,
	ReopenClaimBody,
	SalesAnalysisResponseSchema,
	SalesDataPeriodRequest,
	SalesDataPeriodResponseSchema,
	SalesLossResponse,
	SalesLossResponseSchema,
	SalesSubmissionPeriodResponseSchema,
	SingleClaim,
	SubmitSalesPeriodResponse,
	SubmitSalesPeriodResponseSchema,
	VerifySalesBody,
} from './claims.types';
import { statsQueries } from './stats';

const getClaims = (query: ClaimsQuery): Promise<ClaimsResults> =>
	api
		.get('/claims', {
			params: query,
		})
		.then(parseSchema(ClaimsResults));

const getOwnClaims = (query: ClaimsQuery): Promise<ClaimsResults> =>
	api
		.get('/claims/external', {
			params: query,
		})
		.then(parseSchema(ClaimsResults));

const getClaimById = (claimId: string): Promise<SingleClaim> =>
	api.get(`/claims/claim/${claimId}`).then(parseSchema(SingleClaim));

const getClaimReasons = (): Promise<Reasons> =>
	api.get(`/claims/claimreasons`).then(parseSchema(Reasons));

const getActivityLog = (claimId: string): Promise<Activity> =>
	api.get(`/claims/claim/${claimId}/activity-log`).then(parseSchema(Activity));

const getStatuses = (orgId: number) =>
	api
		.get('/claims/statuses', {
			params: {
				organisationId: orgId,
			},
		})
		.then(parseSchema(ClaimStatuses));

const getReportStatus = (claimId: string) =>
	api
		.get(`/claims/claim/${claimId}/report/status`)
		.then(parseSchema(ReportStatusBody));

const getCalculationTypes = () =>
	api.get('/claims/calculationTypes').then(parseSchema(CalculationTypes));

const getClaimCatCodes = () =>
	api.get('/claims/cat-codes').then(parseSchema(ClaimCatCodes));

const getClaimCauseOfLossCodes = () =>
	api
		.get('/claims/cause-of-loss-codes')
		.then(parseSchema(ClaimCauseOfLossCodes));

const getSalesPeriodData = (claimId: string) =>
	api
		.get(`/claims/claim/${claimId}/sales-period-data`)
		.then(parseSchema(SalesDataPeriodResponseSchema));

const getSalesAnalysis = (claimId: string) =>
	api
		.get(`/claims/claim/${claimId}/sales-analysis`)
		.then(parseSchema(SalesAnalysisResponseSchema));

const getSalesLoss = (claimId: string): Promise<SalesLossResponse> =>
	api
		.get(`/claims/claim/${claimId}/sales-loss`)
		.then(parseSchema(SalesLossResponseSchema));

const getSalesSubmissionStatus = (claimId: string) =>
	api
		.get(`/claims/claim/${claimId}/sales-submission`)
		.then(parseSchema(SalesSubmissionPeriodResponseSchema));

export const claimQueries = {
	all: ['claims'],
	lists: () => [...claimQueries.all, 'list'],
	list: (query: ClaimsQuery) =>
		queryOptions({
			queryKey: [...claimQueries.lists(), query],
			queryFn: () => getClaims(query),
			placeholderData: keepPreviousData,
		}),
	listOwn: (query: ClaimsQuery) =>
		queryOptions({
			queryKey: [...claimQueries.lists(), query, 'own'],
			queryFn: () => getOwnClaims(query),
			placeholderData: keepPreviousData,
		}),
	ids: () => [...claimQueries.all, 'id'],
	id: (
		claimId: string,
		refetch?: {
			refetchInterval?: (claim: SingleClaim) => number | false;
		},
	) =>
		queryOptions({
			queryKey: [...claimQueries.ids(), claimId],
			queryFn: async () => getClaimById(claimId),
			refetchInterval: (query) =>
				query.state.data && refetch?.refetchInterval?.(query.state.data),
		}),
	reportStatus: (
		claimId: string,
		enabled: boolean = true,
		refetch?: {
			refetchInterval?: (claim: ReportStatusBody) => number | false;
		},
	) =>
		queryOptions({
			queryKey: [...claimQueries.ids(), claimId, 'reportStatus'],
			queryFn: async () => getReportStatus(claimId),
			refetchInterval: (query) =>
				query.state.data && refetch?.refetchInterval?.(query.state.data),
			enabled,
		}),
	claimReasons: () =>
		queryOptions({
			queryKey: [...claimQueries.all, 'reasons'],
			queryFn: () => getClaimReasons(),
		}),
	calculationTypes: () =>
		queryOptions({
			queryKey: [...claimQueries.all, 'calculationTypes'],
			queryFn: () => getCalculationTypes(),
		}),
	activity: (claimId: string) =>
		queryOptions({
			queryKey: [...claimQueries.ids(), claimId, 'activity'],
			queryFn: () => getActivityLog(claimId),
		}),
	statuses: (orgId: number) =>
		queryOptions({
			queryKey: [...claimQueries.all, 'statuses', orgId],
			queryFn: () => getStatuses(orgId),
		}),
	catCodes: () =>
		queryOptions({
			queryKey: [...claimQueries.all, 'catCodes'],
			queryFn: () => getClaimCatCodes(),
		}),
	causeOfLossCodes: () =>
		queryOptions({
			queryKey: [...claimQueries.all, 'causeOfLossCodes'],
			queryFn: () => getClaimCauseOfLossCodes(),
		}),
	salesAnalysis: (claimId: string) =>
		queryOptions({
			queryKey: [...claimQueries.ids(), claimId, 'salesAnalysis'],
			queryFn: () => getSalesAnalysis(claimId),
		}),
	salesLoss: (claimId: string) =>
		queryOptions({
			queryKey: [...claimQueries.ids(), claimId, 'salesLoss'],
			queryFn: () => getSalesLoss(claimId),
		}),
	salesPeriodData: (claimId: string) =>
		queryOptions({
			queryKey: [...claimQueries.ids(), claimId, 'salesPeriodData'],
			queryFn: () => getSalesPeriodData(claimId),
		}),
	salesSubmissionStatus: (claimId: string) =>
		queryOptions({
			queryKey: [...claimQueries.ids(), claimId, 'salesSubmissionStatus'],
			queryFn: () => getSalesSubmissionStatus(claimId),
		}),
};

const newClaim = () =>
	api.post(`/claims/claim`, { json: {} }).then(parseSchema(NewClaimResponse));

export const useNewClaimMutate = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: () => newClaim(),
		onSuccess: () =>
			Promise.all([
				queryClient.invalidateQueries({
					queryKey: claimQueries.lists(),
				}),
				queryClient.invalidateQueries({
					queryKey: statsQueries.all,
				}),
			]),
	});
};

const updateClaim = (params: ClaimBody) =>
	api.put(`/claims/claim`, { json: params });

export const useUpdateClaimMutate = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (params: ClaimBody) => updateClaim(params),
		onSuccess: (_, params) =>
			Promise.all([
				queryClient.invalidateQueries({
					queryKey: claimQueries.lists(),
				}),
				queryClient.invalidateQueries({
					queryKey: [...claimQueries.ids(), String(params.claim.claimId)],
				}),
				queryClient.invalidateQueries({
					queryKey: statsQueries.all,
				}),
				queryClient.invalidateQueries({
					queryKey: [
						...claimQueries.ids(),
						String(params.claim.claimId),
						'salesPeriodData',
					],
				}),
				queryClient.invalidateQueries({
					queryKey: [
						...claimQueries.ids(),
						String(params.claim.claimId),
						'salesSubmissionStatus',
					],
				}),
			]),
	});
};

const closeClaim = (claimId: string, payload: CloseClaimBody) =>
	api.put(`/claims/claim/${claimId}/close`, { json: payload });

export const useMutateClaimClose = (claimId: string) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (params: CloseClaimBody) => closeClaim(claimId, params),
		onSuccess: () =>
			Promise.all([
				queryClient.invalidateQueries({
					queryKey: claimQueries.lists(),
				}),
				queryClient.invalidateQueries({
					queryKey: [...claimQueries.ids(), claimId],
				}),
				queryClient.invalidateQueries({
					queryKey: statsQueries.all,
				}),
			]),
	});
};

const optOutClaim = (claimId: string) =>
	api.put(`/claims/claim/${claimId}/optout`);

export const useMutateClaimOptOut = (claimId: string) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: () => optOutClaim(claimId),
		onSuccess: () =>
			Promise.all([
				queryClient.invalidateQueries({
					queryKey: claimQueries.lists(),
				}),
				queryClient.invalidateQueries({
					queryKey: [...claimQueries.ids(), claimId],
				}),
				queryClient.invalidateQueries({
					queryKey: statsQueries.all,
				}),
			]),
	});
};

const optInClaim = (claimId: string) =>
	api.put(`/claims/claim/${claimId}/optin`);

export const useMutateClaimOptIn = (claimId: string) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: () => optInClaim(claimId),
		onSuccess: () =>
			Promise.all([
				queryClient.invalidateQueries({
					queryKey: claimQueries.lists(),
				}),
				queryClient.invalidateQueries({
					queryKey: [...claimQueries.ids(), claimId],
				}),
				queryClient.invalidateQueries({
					queryKey: statsQueries.all,
				}),
			]),
	});
};

const submitReport = (claimId: string, payload: ReportSubmitBody) =>
	api.put(`/claims/claim/${claimId}/report/submit`, { json: payload });

export const useMutateSubmitReport = (claimId: string) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (params: ReportSubmitBody) => submitReport(claimId, params),
		onSuccess: () =>
			Promise.all([
				queryClient.invalidateQueries({
					queryKey: claimQueries.lists(),
				}),
				queryClient.invalidateQueries({
					queryKey: [...claimQueries.ids(), claimId],
				}),
				queryClient.invalidateQueries({
					queryKey: statsQueries.all,
				}),
			]),
	});
};

const reopenClaim = (claimId: string, payload: ReopenClaimBody) =>
	api.put(`/claims/claim/${claimId}/reopen`, { json: payload });

export const useMutateClaimReopen = (claimId: string) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (params: ReopenClaimBody) => reopenClaim(claimId, params),
		onSuccess: () =>
			Promise.all([
				queryClient.invalidateQueries({
					queryKey: claimQueries.lists(),
				}),
				queryClient.invalidateQueries({
					queryKey: [...claimQueries.ids(), claimId],
				}),
				queryClient.invalidateQueries({
					queryKey: statsQueries.all,
				}),
			]),
	});
};

const getExtractFile = (claimId: number, subset?: 'details' | 'data') =>
	api.get(
		`/file/${
			subset === 'details' ? 'extract-claim-details'
			: subset === 'data' ? 'extract-claim-data'
			: 'extract-files'
		}/${claimId}`,
	);

export const downloadExtractClaimFile = async (
	claimId: number,
	subset?: 'details' | 'data',
) => {
	const response = await getExtractFile(claimId, subset);

	const filename = parse(response.headers.get('content-disposition') as string)
		.parameters.filename;
	const type =
		(response.headers.get('content-type') as string) || 'application/zip';

	return new File(
		[await response.blob()],
		(typeof filename === 'string' && filename) ||
			`claim-${subset ?? 'extract'}-${claimId}.zip`,
		{ type },
	);
};

const getReportFile = (claimId: string) => api.get(`/file/report/${claimId}`);

export const downloadReportClaimFile = async (claimId: string) => {
	const response = await getReportFile(claimId);

	const filename = parse(response.headers.get('content-disposition') as string)
		.parameters.filename;
	const type =
		(response.headers.get('content-type') as string) || 'application/zip';

	return new File(
		[await response.blob()],
		(typeof filename === 'string' && filename) || `claim-report-${claimId}.zip`,
		{ type },
	);
};

const bulkAssignClaims = (claimIds: number[], handlerId: string) =>
	api
		.put('/claims/bulkassign', {
			json: { claimIds, claimsHandlerId: handlerId },
		})
		.catch(handleError(['claimIds', 'claimsHandlerId']));

export const useBulkAssignClaimsMutate = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (params: { claimIds: number[]; handlerId: string }) =>
			bulkAssignClaims(params.claimIds, params.handlerId),
		onSuccess: (_, variables) =>
			Promise.all([
				queryClient.invalidateQueries({
					queryKey: claimQueries.lists(),
				}),
				...variables.claimIds.map((claimId) =>
					queryClient.invalidateQueries({
						queryKey: [...claimQueries.ids(), String(claimId)],
					}),
				),
			]),
	});
};

const continueClaim = (claimId: string) =>
	api.put(`/claims/claim/${claimId}/continue`).catch(handleError([]));

export const useContinueClaimMutate = (claimId: string) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: () => continueClaim(claimId),
		onSuccess: () =>
			Promise.all([
				queryClient.invalidateQueries({
					queryKey: claimQueries.lists(),
				}),
				queryClient.invalidateQueries({
					queryKey: [...claimQueries.ids(), claimId],
				}),
			]),
	});
};

const putSalesData = (claimId: string, data: SalesDataPeriodRequest) =>
	api
		.put(`/claims/claim/${claimId}/sales-period-data`, {
			json: data,
		})
		.catch(handleError([]));

export const usePutSalesDataMutate = (claimId: string) => {
	return useMutation({
		mutationFn: (data: SalesDataPeriodRequest) => putSalesData(claimId, data),
	});
};

const submitSalesPeriods = (claimId: string) =>
	api
		.post(`/claims/claim/${claimId}/submit-sales-periods`)
		.then(parseSchema(SubmitSalesPeriodResponseSchema))
		.catch(handleError([]));

const verifySales = (claimId: string, payload: VerifySalesBody) =>
	api.post(`/claims/claim/${claimId}/verify-sales`, { json: payload });

export const useVerifySalesMutate = (
	claimId: string,
	options?: { invalidateOnSuccess?: boolean },
) => {
	const invalidateOnSuccess = options?.invalidateOnSuccess ?? true;
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (payload: VerifySalesBody) => verifySales(claimId, payload),
		onSuccess: () =>
			invalidateOnSuccess ?
				Promise.all([
					queryClient.invalidateQueries({
						queryKey: [...claimQueries.ids(), claimId],
					}),
					queryClient.invalidateQueries({
						queryKey: [...claimQueries.ids(), claimId, 'salesPeriodData'],
					}),
					queryClient.invalidateQueries({
						queryKey: [...claimQueries.ids(), claimId, 'salesSubmissionStatus'],
					}),
				])
			:	undefined,
	});
};

export const useSubmitSalesPeriodDataMutate = (
	claimId: string,
	onSuccess: (arg: SubmitSalesPeriodResponse) => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: () => submitSalesPeriods(claimId),
		onSuccess: (data) => {
			onSuccess(data);
			return Promise.all([
				queryClient.invalidateQueries({
					queryKey: [...claimQueries.ids(), claimId],
				}),
				queryClient.invalidateQueries({
					queryKey: [...claimQueries.ids(), claimId, 'salesPeriodData'],
				}),
				queryClient.invalidateQueries({
					queryKey: [...claimQueries.ids(), claimId, 'salesSubmissionStatus'],
				}),
			]);
		},
	});
};
